import React, { useRef, useState, useCallback, useEffect } from 'react';
import { getCharInfo, cycleVowelForward, cycleVowelBackward } from '../geezData';
import { isPeriodLike, isSpaceLike, splitWords } from '../utils/textSpacing';

export default function RichEditor({
  text,
  onTextChange,
  selectedRange,
  onSelectRange,
}) {
  const editorRef = useRef(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  // Synchronize selection, refocus, and maintain cursor integrity on external updates
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Only update DOM selection if it actually differs from current DOM state
    if (editor.selectionStart !== selectedRange.start || editor.selectionEnd !== selectedRange.end) {
      editor.setSelectionRange(selectedRange.start, selectedRange.end);
      setSelection(selectedRange);
      editor.focus();
    }
  }, [selectedRange]);

  const handleInput = useCallback((e) => {
    onTextChange(e.target.value);
  }, [onTextChange]);

  const handleSelect = useCallback((e) => {
    const start = e.target.selectionStart || 0;
    const end = e.target.selectionEnd || 0;
    setSelection({ start, end });
    onSelectRange({ start, end });
  }, [onSelectRange]);

  const cycleVowel = useCallback((cycleFn) => {
    const editor = editorRef.current;
    if (!editor) return;
    const pos = editor.selectionStart;
    const char = text[pos - 1];

    if (char && getCharInfo(char)) {
      const newChar = cycleFn(char);
      const newText = text.substring(0, pos - 1) + newChar + text.substring(pos);
      onTextChange(newText);
      setTimeout(() => {
        editor.setSelectionRange(pos, pos);
      }, 0);
    }
  }, [text, onTextChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      cycleVowel(cycleVowelBackward);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      cycleVowel(cycleVowelForward);
    }
  }, [cycleVowel]);

  const words = splitWords(text);

  const renderHighlightedText = () => {
    const chars = [...text];
    return (
      <span className="editor-visual-text">
        {chars.map((ch, i) => {
          const info = getCharInfo(ch);
          const isSelected = selection.start <= i && selection.end > i;
          const cls = [
            'visual-char',
            info && 'geez-char',
            isSelected && 'char-selected',
            isSpaceLike(ch) && 'char-space',
            isPeriodLike(ch) && 'char-period',
          ].filter(Boolean).join(' ');

          return (
            <span key={i} className={cls} title={info ? info.fullIPA : ''}>
              {ch}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <div className="rich-editor">
      <div className="editor-toolbar">
        <span className="editor-stats">
          {[...text].length} chars
          {words.length > 0 && ` · ${words.length} words`}
        </span>
      </div>

      <div className="editor-wrapper">
        <textarea
          ref={editorRef}
          className="editor-textarea"
          value={text}
          onChange={handleInput}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          placeholder="Type Ge'ez here, or use the Keyboard/Drawing pad tool..."
          rows={6}
          spellCheck={false}
        />
      </div>

      {renderHighlightedText()}
    </div>
  );
}

export { RichEditor };